import { action, computed, makeObservable, observable } from 'mobx'

import { ITileset, UploadingType } from '~/client/graph'
import MapboxService from '~/client/src/shared/services/MapboxService'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import Guard from '~/client/src/shared/utils/Guard'

import { withSelfHostedMapboxData } from '../../utils/selfHostedTileset'
import { toast } from '../../utils/toast'
import { FileUploadingStore } from './FileUploading.store'
import TilesetUploadProgressStore from './TilesetUploadProgress.store'

// Cross-tab delete sync (STX1-839). A tileset delete goes through a REST
// endpoint plus a local store removal only; unlike other entities it has no
// GraphQL delete mutation the backend re-broadcasts on ListenTilesetsByProjectId,
// so a delete on one tab never reaches the others. This same-origin channel
// mirrors the removal: the acting tab posts the removed id and peer tabs apply
// it through the same receiveOne(id, null) path the subscription already uses.
//
// This is a same-browser INTERIM. The canonical fix is a GraphQL deleteTileset
// mutation the backend re-broadcasts on the subscription (as RemoteTilesets does
// via DeleteRemoteTilesets + ListenRemoteTilesetsByProjectId) — that also covers
// cross-user/cross-browser and lets this channel be deleted. It needs backend
// support, so it is out of scope for this frontend-only change.
const TILESET_SYNC_CHANNEL = 'stx-tileset-sync'

interface ITilesetRemovedMessage {
  type: 'tileset-removed'
  id: string
}

export default class TilesetsStore {
  @observable public isDataReceived: boolean = false
  private tilesetSyncChannel: BroadcastChannel | null = null
  // Ids removed this session (locally or via a cross-tab BroadcastChannel
  // delete). There is no backend delete event, so both the initial
  // GetMapboxTilesets snapshot and any late/in-flight ListenTilesetsByProjectId
  // update can still carry a just-deleted tileset and would otherwise resurrect
  // it. Both receiveList and receiveOne suppress these ids. A tileset id is a
  // retired, globally-unique ObjectId — it is never legitimately re-created (a
  // re-upload gets a new id) — so suppressing it for the session is safe.
  private readonly removedIds: Set<string> = new Set()

  public constructor(
    private readonly eventsStore: EventsStore,
    private readonly fileUploadingStore: FileUploadingStore,
    private readonly mapboxService: MapboxService,
    private readonly uploadProgressStore: TilesetUploadProgressStore,
  ) {
    makeObservable(this)
    Guard.requireAll({
      eventsStore,
      fileUploadingStore,
      mapboxService,
      uploadProgressStore,
    })
    this.setupBroadcastSync()
  }

  // Same-origin cross-tab sync so a delete on one tab removes the tileset on the
  // others without a refresh. Feature-detected and defensively constructed like
  // StruxHubAuthService; degrades to the prior behaviour where BroadcastChannel
  // is unavailable (older Safari, jsdom/SSR).
  private setupBroadcastSync(): void {
    if (typeof BroadcastChannel === 'undefined') return

    try {
      this.tilesetSyncChannel = new BroadcastChannel(TILESET_SYNC_CHANNEL)
    } catch {
      this.tilesetSyncChannel = null
      return
    }

    this.tilesetSyncChannel.onmessage = event => {
      const data = event.data as ITilesetRemovedMessage | null
      if (!data || data.type !== 'tileset-removed' || !data.id) return
      // Apply unconditionally rather than gating on the tab's active project:
      // receiveOne is idempotent (deleting an absent id is a no-op), and tileset
      // ids are globally unique, so a peer on another project is unaffected. A
      // project-id check here would instead drop the event whenever this tab is
      // still initializing/switching, leaving the deleted tile stranded.
      this.receiveOne(data.id, null)
    }
  }

  @computed
  public get list(): ITileset[] {
    return Array.from(this.byId.values())
  }

  public get sortedList(): ITileset[] {
    return this.list.sort((a, b) => b?.createdAt - a?.createdAt)
  }

  public get byId(): Map<string, ITileset> {
    const { tilesets } = this.eventsStore.appState
    return tilesets
  }

  @action.bound
  public clearList() {
    this.byId.clear()
    this.isDataReceived = false
  }

  @action.bound
  public receiveList(list: ITileset[]) {
    this.clearList()

    list.forEach(dto => {
      // Skip ids removed cross-tab before/between loads — the snapshot can be
      // stale (no backend delete event), so re-adding them would resurrect a
      // deleted tileset until the next refresh.
      if (dto?.id && this.removedIds.has(dto.id)) return
      this.byId.set(dto?.id, withSelfHostedMapboxData(dto))
    })
    this.isDataReceived = true
  }

  @action.bound
  public async removeOne(
    tilesetId: string,
    callbackFn?: () => void,
    errorCallbackFn?: () => void,
  ) {
    if (!this.byId.has(tilesetId)) {
      return
    }

    try {
      await this.mapboxService.deleteTileset(tilesetId)
      // Route through receiveOne so the id is tombstoned locally too — a list
      // load in flight during the delete must not resurrect it.
      this.receiveOne(tilesetId, null)
      // Only after a confirmed delete — never on the catch path, or peer tabs
      // would evict a tile the backend still has.
      this.broadcastRemoval(tilesetId)
      callbackFn?.()
    } catch {
      toast.error('Error deleting tileset')
      errorCallbackFn?.()
    }
  }

  private broadcastRemoval(tilesetId: string): void {
    const message: ITilesetRemovedMessage = {
      type: 'tileset-removed',
      id: tilesetId,
    }
    try {
      this.tilesetSyncChannel?.postMessage(message)
    } catch {
      // Channel closed / serialization failure — non-fatal; peer tabs catch up
      // on their next refresh.
    }
  }

  @action.bound
  public receiveOne(id: string, tileset: ITileset): void {
    if (!tileset) {
      this.removedIds.add(id)
      this.byId.delete(id)
      return
    }
    // Ignore a present record for an id already removed this session: a late
    // subscription update (e.g. a queued/processing/success event emitted
    // before the delete) must not resurrect the deleted tileset, and its id is
    // never legitimately reused.
    if (this.removedIds.has(id)) return
    this.byId.set(id, withSelfHostedMapboxData(tileset))
  }

  @action.bound
  public async uploadTileset(
    file: Blob,
    fileName?: string,
    callbackFn?: () => void,
    errorCallbackFn?: () => void,
  ) {
    const { id: projectId } = this.eventsStore.appState.activeProject

    // Start tracking the upload (vector files use a different upload path without granular progress)
    const uploadId = this.uploadProgressStore.startUpload(
      fileName || 'tileset',
      file.size,
    )

    try {
      const [fileInfo] = await this.fileUploadingStore.uploadFile(
        file,
        UploadingType.Tileset,
        projectId,
        fileName,
      )

      // Mark as notifying backend
      this.uploadProgressStore.setNotifyingBackend(uploadId)

      await this.mapboxService.uploadTilesetFile(
        projectId,
        fileName,
        fileInfo.filePath,
      )

      // Mark as complete
      this.uploadProgressStore.completeUpload(uploadId)
      callbackFn?.()
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed'
      this.uploadProgressStore.setError(uploadId, errorMessage)
      toast.error('Error uploading map file')
      errorCallbackFn?.()
    }
  }

  @action.bound
  public async uploadGeoTIFF(
    file: Blob,
    fileName?: string,
    callbackFn?: () => void,
    errorCallbackFn?: () => void,
  ) {
    const { id: projectId } = this.eventsStore.appState.activeProject

    // Start tracking the upload. GeoTIFFs now go through the same googleStorage path as vector files
    // (no granular progress): the backend tiles them via MTS — including 8-bit conversion and chunking —
    // instead of the legacy Mapbox Uploads API.
    const uploadId = this.uploadProgressStore.startUpload(
      fileName || 'tileset',
      file.size,
    )

    try {
      const [fileInfo] = await this.fileUploadingStore.uploadFile(
        file,
        UploadingType.Tileset,
        projectId,
        fileName,
      )

      // Mark as notifying backend
      this.uploadProgressStore.setNotifyingBackend(uploadId)

      // Same endpoint as vector files; the backend routes a .tif/.tiff filename into the GeoTIFF MTS pipeline.
      await this.mapboxService.uploadTilesetFile(
        projectId,
        fileName,
        fileInfo.filePath,
      )

      // Mark as complete
      this.uploadProgressStore.completeUpload(uploadId)
      callbackFn?.()
    } catch (error) {
      // Skip error handling if upload was cancelled (removed from map)
      if (!this.uploadProgressStore.uploads.has(uploadId)) {
        return
      }
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed'
      this.uploadProgressStore.setError(uploadId, errorMessage)
      toast.error('Error uploading map file')
      errorCallbackFn?.()
    }
  }
}
